import React, { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { graphql } from 'gatsby';
import { CheckboxGroup } from '@keytrade/components-checkedinput';
import { Pagination } from '@keytrade/components-pagination';

import FilterPill from '@/components/filters/FilterPill';
import useMediaItems from '@/hooks/useMediaItems';
import useTranslations from '@/hooks/useTranslations';
import translationsKeys from '@/intl/en.json';
import { isDarkTheme, getBackgroundColor } from '@/utils/StyleUtils';
import { config } from '@keytrade/functions';
import BlockWrapper from '@/components/BlockWrapper';

import { MediaItemType } from './MediaItem';
import MediaListing from './MediaListing';
import MobileMediaFilters from './MobileMediaFilters';
import {
  Section,
  SectionWrapper,
  ContentWrapper,
  ItemsWrapper,
  Heading,
  Title,
  Description,
  FilterContainer,
  FilterBlock,
  FilterPills,
  ClearFilters,
  TotalAmount,
} from './MediaList.styled';
import { MediaListThemeContext } from './MediaListContext';
import { ThemeProvider } from 'styled-components';

const { types, topics } = translationsKeys.media;
const langsKeys = Object.keys(translationsKeys.languages);
const typesKeys = Object.keys(types);
const topicsKeys = Object.keys(topics);

const { list: colors } = config.colors;

export type MediaListType = {
  contenful_id: string;
  node_locale: string;
  name: string;
  title: string;
  description: {
    description: string;
  };
  mediaListTags: string[];
  featuredItem?: MediaItemType;
  displayFilters: boolean;
  mediaItemsPerPageCount?: number;
  backgroundColor?: string;
};

type Props = MediaListType;

const getPagesCount = (items, itemsPerPage) => {
  if (!items.length || !itemsPerPage) {
    return 0;
  }

  return Math.ceil(items.length / itemsPerPage);
};

const getPagedItems = (items, currentPage, itemsPerPage) => {
  if (!itemsPerPage) {
    return items;
  }

  return items.slice(
    itemsPerPage * (currentPage - 1),
    itemsPerPage * currentPage,
  );
};

const MediaList: React.FC<Props> = (props: Props) => {
  const {
    id,
    node_locale: locale,
    mediaListTags: tags,
    displayFilters: isMediaWall,
    description: descriptionObject,
    title,
    featuredItem,
    mediaItemsPerPageCount,
    backgroundColor,
  } = props;

  const { t, to } = useTranslations();

  const [currentPage, setCurrentPage] = useState(1);
  const mediaListingRef = useRef(null);

  const { description } = descriptionObject || {};

  const items = useMediaItems({
    tags,
    locales: isMediaWall ? undefined : [locale],
    featuredItem,
    isMediaWall,
  });

  const onPageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    mediaListingRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const [filteredItems, setFilteredItems] = useState<MediaItemType[]>(
    items || [],
  );

  const [langFilters, setLangFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [topicFilters, setTopicFilters] = useState<string[]>([]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const isThemeDark = isDarkTheme(backgroundColor);

  // pass isThemeDark state to all children with useContext

  const updateHasActiveFilters = () =>
    setHasActiveFilters(
      !!langFilters.length || !!typeFilters.length || !!topicFilters.length,
    );

  useEffect(() => {
    let itemsToDisplay = items;

    if (langFilters.length) {
      itemsToDisplay = itemsToDisplay.filter((item) =>
        langFilters.includes(item.node_locale),
      );
    }

    if (typeFilters.length) {
      itemsToDisplay = itemsToDisplay.filter((item) =>
        typeFilters.includes(item.type),
      );
    }

    if (topicFilters.length) {
      itemsToDisplay = itemsToDisplay.filter((item) =>
        item.topics?.some((topic) =>
          topicFilters.includes(topic.toLocaleLowerCase()),
        ),
      );
    }

    updateHasActiveFilters();
    setCurrentPage(1);
    setFilteredItems(itemsToDisplay);
  }, [langFilters, typeFilters, topicFilters]);

  const { langs, types, topics } = useMemo(() => {
    const langs = langsKeys.map((l) => to(`languages.${l}`, l));
    const types = typesKeys.map((t) => to(`media.types.${t}`, t));
    const topics = topicsKeys.map((t) => to(`media.topics.${t}`, t));

    return { langs, types, topics };
  }, [langsKeys, typesKeys, topicsKeys, to]);

  const handleFilters = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    if (!value) {
      return;
    }

    setter((prevState) =>
      prevState.includes(value)
        ? prevState.filter((item) => item !== value)
        : [...prevState, value],
    );
  };

  const handleLangFilters = (event) =>
    handleFilters(event.target.value, setLangFilters);
  const handleTypeFilters = (event) =>
    handleFilters(event.target.value, setTypeFilters);
  const handleTopicFilters = (event) =>
    handleFilters(event.target.value, setTopicFilters);

  const handleSetFilters = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((oldValue) => oldValue.filter((item) => item !== value));
  };

  const handleSetLangsFilter = (value: string) =>
    handleSetFilters(value, setLangFilters);
  const handleSetTypesFilter = (value: string) =>
    handleSetFilters(value, setTypeFilters);
  const handleSetTopicsFilter = (value: string) =>
    handleSetFilters(value, setTopicFilters);

  const clearFilters = () => {
    setLangFilters([]);
    setTypeFilters([]);
    setTopicFilters([]);
  };

  useEffect(() => {
    if (isMediaWall) {
      setLangFilters([locale]);
    }
  }, []);

  const pagesCount = getPagesCount(filteredItems, mediaItemsPerPageCount);
  const pagedItems = getPagedItems(
    filteredItems,
    currentPage,
    mediaItemsPerPageCount,
  );

  return (
    <MediaListThemeContext.Provider value={isThemeDark}>
      <ThemeProvider theme={isThemeDark ? { dark: true } : { dark: false }}>
        <BlockWrapper id={id} color={backgroundColor} padding='none'>
          <Section color={getBackgroundColor(backgroundColor)}>
            <SectionWrapper isWide={isMediaWall}>
              <Heading>
                <Title
                  level='h3'
                  size='xl'
                  color={isThemeDark ? 'White' : 'BlueMoon'}
                >
                  {title}
                </Title>
                <Description
                  weight='semiBold'
                  margin='1.6rem 0 0'
                  color={isThemeDark ? 'White' : 'BlueMoon'}
                >
                  {description}
                </Description>
              </Heading>
              <ContentWrapper>
                {isMediaWall && (
                  <>
                    <MobileMediaFilters
                      languages={{
                        content: langs,
                        handleFilter: handleLangFilters,
                        contentSelected: langFilters,
                      }}
                      types={{
                        content: types,
                        handleFilter: handleTypeFilters,
                        contentSelected: typeFilters,
                      }}
                      topics={{
                        content: topics,
                        handleFilter: handleTopicFilters,
                        contentSelected: topicFilters,
                      }}
                      clearMobileFilters={clearFilters}
                    />
                    <FilterContainer>
                      <CheckboxGroup
                        name='Languages'
                        label={t('media.filters.languages')}
                        options={langs}
                        value={langFilters}
                        onChange={handleLangFilters}
                        errorMessage=''
                        margin='0 0 42px 0'
                      />
                      <CheckboxGroup
                        name='Types'
                        label={t('media.filters.types')}
                        options={types}
                        value={typeFilters}
                        onChange={handleTypeFilters}
                        errorMessage=''
                        margin='0 0 42px 0'
                      />
                      <CheckboxGroup
                        name='Topics'
                        label={t('media.filters.topics')}
                        options={topics}
                        value={topicFilters}
                        onChange={handleTopicFilters}
                        errorMessage=''
                        margin='0 0 42px 0'
                      />
                    </FilterContainer>
                  </>
                )}
                <ItemsWrapper>
                  {hasActiveFilters && (
                    <FilterBlock isDark={isThemeDark}>
                      <FilterPills>
                        {langFilters.map((val) => (
                          <FilterPill
                            key={val}
                            value={val}
                            type='languages'
                            removeFilter={() => handleSetLangsFilter(val)}
                          />
                        ))}
                        {typeFilters.map((val) => (
                          <FilterPill
                            key={val}
                            componentType='media'
                            value={val}
                            type='types'
                            removeFilter={() => handleSetTypesFilter(val)}
                          />
                        ))}
                        {topicFilters.map((val) => (
                          <FilterPill
                            key={val}
                            componentType='media'
                            value={val}
                            type='topics'
                            removeFilter={() => handleSetTopicsFilter(val)}
                          />
                        ))}

                        <ClearFilters onClick={clearFilters}>
                          {t('filter.clear_all')}
                        </ClearFilters>
                      </FilterPills>
                      <TotalAmount isDark={isThemeDark}>
                        {`${filteredItems.length} ${t(
                          filteredItems?.length === 1
                            ? 'filter.matches_single'
                            : 'filter.matches',
                        )}`}
                      </TotalAmount>
                    </FilterBlock>
                  )}
                  <MediaListing
                    mediaRef={mediaListingRef}
                    featuredItem={isMediaWall ? null : featuredItem}
                    items={pagedItems}
                    isMediaWall={isMediaWall}
                    isDark={isThemeDark}
                  />
                  <Pagination
                    numberPages={pagesCount}
                    selectedPage={currentPage}
                    onClick={onPageClick}
                    transparentBackground={true}
                    prevAccessibilityLabel={t('previous')}
                    nextAccessibilityLabel={t('next')}
                    margin={'3.2rem auto 0'}
                    isDark={isThemeDark}
                  />
                </ItemsWrapper>
              </ContentWrapper>
            </SectionWrapper>
          </Section>
        </BlockWrapper>
      </ThemeProvider>
    </MediaListThemeContext.Provider>
  );
};

export default MediaList;

export const MediaListQuery = graphql`
  fragment MediaListFragment on ContentfulMediaList {
    contentful_id
    id
    node_locale
    __typename
    name
    title
    description {
      description
    }
    mediaListTags: tags
    featuredItem {
      ...MediaItemFragment
    }
    displayFilters
    mediaItemsPerPageCount
    backgroundColor
  }
`;
